'use client';

import React, { useState, useCallback, useEffect } from 'react';
import dynamic from 'next/dynamic';
import FloatingSearchBar from './FloatingSearchBar';
import FloatingMapControls from './FloatingMapControls';
import AddRateFAB from './AddRateFAB';
import MapLoadingSkeleton from './MapLoadingSkeleton';
import AddRateBottomSheet from './AddRateBottomSheet';
import ActivityThread from './ActivityThread';
import { MapPin, Check, X } from 'lucide-react';

// Dynamically import the map with ssr: false to prevent hydration errors
const MapCanvas = dynamic(() => import('./MapCanvas'), {
  ssr: false,
  loading: () => <MapLoadingSkeleton />,
});

export type MapMode = 'map' | 'satellite';

export default function HomepageMapViewInner() {
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [pinnedCoords, setPinnedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapMode, setMapMode] = useState<MapMode>('map');

  // Draggable pin mode — triggered by "Search on Map" from the locality search
  const [draggablePinMode, setDraggablePinMode] = useState(false);
  const [draggablePinCoords, setDraggablePinCoords] = useState<{ lat: number; lng: number } | null>(null);
  // Preserve locality query across Search on Map flow
  const [savedLocality, setSavedLocality] = useState('');

  // Pre-fill data for "Correct Rate" flow
  const [correctRateLocality, setCorrectRateLocality] = useState('');
  const [correctRateValue, setCorrectRateValue] = useState('');
  const [correctRateLandType, setCorrectRateLandType] = useState<'Registered' | 'Unregistered' | ''>('');
  const [initialRate, setInitialRate] = useState('');
  const [initialLandType, setInitialLandType] = useState('');

  const handleLongPress = useCallback((lat: number, lng: number) => {
    setPinnedCoords({ lat, lng });
    setBottomSheetOpen(true);
  }, []);

  const handleFABOpen = useCallback(() => {
    setPinnedCoords(null);
    setBottomSheetOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setBottomSheetOpen(false);
    setPinnedCoords(null);
    setCorrectRateLocality('');
    setCorrectRateValue('');
    setCorrectRateLandType('');
    if (typeof window !== 'undefined' && (window as any).__clearDroppedPin) {
      (window as any).__clearDroppedPin();
    }
  }, []);

  // Called when user clicks "Search on Map" from the locality no-results state
  const handleSearchOnMap = useCallback((localityQuery: string) => {
    // Save the locality query so it can be restored when the sheet reopens
    setSavedLocality(localityQuery);
    // Bottom sheet already animates closed before calling this
    setBottomSheetOpen(false);
    setPinnedCoords(null);
    setCorrectRateLocality('');
    setCorrectRateValue('');
    setCorrectRateLandType('');
    if (typeof window !== 'undefined' && (window as any).__clearDroppedPin) {
      (window as any).__clearDroppedPin();
    }
    // Enter draggable pin mode — start pin at map center
    setDraggablePinCoords(null);
    setDraggablePinMode(true);
  }, []);

  // Called when draggable pin is moved
  const handleDraggablePinMove = useCallback((lat: number, lng: number) => {
    setDraggablePinCoords({ lat, lng });
  }, []);

  // Confirm the draggable pin location → open bottom sheet with those coords
  const handleConfirmDraggablePin = useCallback(() => {
    if (!draggablePinCoords) return;
    setDraggablePinMode(false);
    setPinnedCoords(draggablePinCoords);
    setBottomSheetOpen(true);
  }, [draggablePinCoords]);

  // Cancel draggable pin mode → go back to bottom sheet without coords
  const handleCancelDraggablePin = useCallback(() => {
    setDraggablePinMode(false);
    setDraggablePinCoords(null);
    if (typeof window !== 'undefined' && (window as any).__clearDraggablePin) {
      (window as any).__clearDraggablePin();
    }
    // Re-open the bottom sheet, restoring saved locality
    setPinnedCoords(null);
    setBottomSheetOpen(true);
  }, []);

  // Expose global handler for "Correct Rate" from PriceMarker popups
  React.useEffect(() => {
    (window as any).__openCorrectRate = (data: {
      locality?: string;
      rate?: string;
      landType?: 'Registered' | 'Unregistered' | '';
      lat?: number;
      lng?: number;
    }) => {
      setCorrectRateLocality(data.locality ?? '');
      setCorrectRateValue(data.rate ?? '');
      setCorrectRateLandType(data.landType ?? '');
      if (data.lat != null && data.lng != null) {
        setPinnedCoords({ lat: data.lat, lng: data.lng });
      } else {
        setPinnedCoords(null);
      }
      setSavedLocality('');
      setBottomSheetOpen(true);
    };
    return () => {
      delete (window as any).__openCorrectRate;
    };
  }, []);

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-slate-100">
      {/* Full-screen map base */}
      <MapCanvas
        onLongPress={handleLongPress}
        mapMode={mapMode}
        draggablePinMode={draggablePinMode}
        onDraggablePinMove={handleDraggablePinMove}
      />

      {/* Floating Search Bar — top center */}
      <FloatingSearchBar mapMode={mapMode} onMapModeChange={setMapMode} />

      {/* Floating Map Controls — right edge */}
      <FloatingMapControls />

      {/* Real-time activity thread — top right */}
      <ActivityThread />

      {/* Floating Add Rate FAB — bottom center (hidden in draggable pin mode) */}
      {!draggablePinMode && <AddRateFAB onOpen={handleFABOpen} />}

      {/* Long press hint — bottom center above FAB (hidden in draggable pin mode) */}
      {!draggablePinMode && (
        <div
          className="absolute bottom-24 left-1/2 -translate-x-1/2 z-[900] pointer-events-none"
          style={{ whiteSpace: 'nowrap' }}
        >
          <span className="inline-flex items-center gap-1.5 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm">
            💡 Long press on the map to add a new rate
          </span>
        </div>
      )}

      {/* Draggable pin mode overlay */}
      {draggablePinMode && (
        <>
          {/* Fixed center pin — always at screen center, follows map pan */}
          <div
            className="absolute inset-0 flex items-center justify-center z-[1050] pointer-events-none"
            style={{ paddingBottom: '44px' }} // offset so pin tip points to exact center
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              filter: 'drop-shadow(0 4px 10px rgba(0,86,210,0.55))',
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#0056D2',
                borderRadius: '50% 50% 50% 0',
                transform: 'rotate(-45deg)',
                border: '3px solid #fff',
                boxShadow: '0 2px 10px rgba(0,86,210,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  background: '#fff',
                  borderRadius: '50%',
                  transform: 'rotate(45deg)',
                }} />
              </div>
              <div style={{
                width: '8px',
                height: '8px',
                background: 'rgba(0,86,210,0.3)',
                borderRadius: '50%',
                marginTop: '2px',
              }} />
            </div>
          </div>

          {/* Instruction banner */}
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 z-[1100] pointer-events-none"
            style={{ whiteSpace: 'nowrap' }}
          >
            <span className="inline-flex items-center gap-2 bg-black/70 text-white text-xs font-semibold px-4 py-2 rounded-full backdrop-blur-sm shadow-lg">
              <MapPin size={13} className="text-yellow-300" />
              Pan the map to position the pin
            </span>
          </div>

          {/* Confirm / Cancel buttons */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-[1100] flex items-center gap-3">
            <button
              onClick={handleCancelDraggablePin}
              className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white text-gray-700 text-sm font-semibold shadow-lg border border-gray-200 hover:bg-gray-50 active:scale-95 transition-all duration-150"
            >
              <X size={16} />
              Cancel
            </button>
            <button
              onClick={handleConfirmDraggablePin}
              disabled={!draggablePinCoords}
              className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-white text-sm font-semibold shadow-lg hover:bg-accent active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check size={16} />
              Confirm Location
            </button>
          </div>
        </>
      )}

      {/* Bottom sheet — shared between FAB and long-press */}
      <AddRateBottomSheet
        open={bottomSheetOpen}
        onClose={handleClose}
        pinnedLat={pinnedCoords?.lat}
        pinnedLng={pinnedCoords?.lng}
        onSearchOnMap={handleSearchOnMap}
        initialLocality={correctRateLocality || savedLocality}
        initialRate={correctRateValue}
        initialLandType={correctRateLandType}
      />
    </div>
  );
}